import { Link } from "react-router-dom";
import { BRAND, CHAIN } from "@shared/copy";
import { SearchBox } from "../components/SearchBox";

export function Home() {
  return (
    <>
      <section className="hero">
        <img className="hero-img" src="/images/hero.jpg" alt="Tropical river floodplain at dusk" />
        <div className="hero-shade" />
        <div className="contours" />
        <div className="water-line" style={{ top: "58%" }} />
        <div className="water-line" style={{ top: "64%", animationDelay: "2s" }} />
        <div className="hero-copy">
          <p className="kicker">Floodplains.net · Built on OneNetwork</p>
          <h1>{BRAND.hero}</h1>
          <p className="lede">
            Floodplains connects the models, studies, assumptions, observations, events, and decisions that shape what we
            know about flooding.
          </p>
          <div className="hero-actions">
            <Link className="btn" to="/system/ross-river">
              Explore a catchment
            </Link>
            <Link className="btn btn-ghost" to="/search">
              Search models &amp; studies
            </Link>
          </div>
          <SearchBox />
          <p className="proof">{BRAND.proof}</p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <p className="kicker">The problem</p>
          <h2>A flood model is more than its map.</h2>
          <p className="lede">
            Every model contains assumptions, judgement, calibration choices, inherited data, limitations, and
            institutional memory. Much of that knowledge is lost when teams change or reports are archived. Floodplains
            keeps the chain visible.
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker">What Floodplains connects</p>
          <div className="chain">
            {CHAIN.map((n, i) => (
              <span key={n}>
                {n}
                {i < CHAIN.length - 1 ? <i> → </i> : null}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap grid-3">
          <article className="card">
            <p className="kicker">Model Observatory</p>
            <h3>See how the model became the model.</h3>
            <p className="muted">
              Trace versions, terrain, calibration events, assumptions, validation, superseded studies, and the
              decisions that depend on them.
            </p>
            <p><Link to="/model/ross-river-tuflow-2021">Open a model record</Link></p>
          </article>
          <article className="card">
            <p className="kicker">Assumptions</p>
            <h3>Make judgement visible.</h3>
            <p className="muted">
              Engineering judgement is unavoidable. Undocumented judgement is not. Floodplains records the value,
              rationale, range, evidence, sensitivity, and provenance.
            </p>
            <p><Link to="/assumptions">Assumption atlas</Link></p>
          </article>
          <article className="card">
            <p className="kicker">Institutional memory</p>
            <h3>The knowledge stays.</h3>
            <p className="muted">
              Capture the context held by experienced practitioners before it disappears into an inbox, archive, or
              former employee&apos;s memory.
            </p>
            <p><Link to="/workspace/gaps">Knowledge gaps</Link></p>
          </article>
        </div>
      </section>

      <section className="section">
        <div className="wrap grid-2">
          <article>
            <p className="kicker">From system intelligence to place decisions</p>
            <h2>Floodplains.net is not Floodplain.com.au.</h2>
            <p className="muted">
              Floodplains.net maintains catchment and model intelligence. Floodplain.com.au applies that intelligence to
              individual sites, assets, and projects. OneNetwork holds the shared objects.
            </p>
            <p>
              <a href="https://floodplain.com.au">floodplain.com.au</a>
              {" · "}
              <Link to="/methodology">Methodology</Link>
              {" · "}
              <Link to="/data">Data</Link>
            </p>
          </article>
          <article className="card">
            <p className="kicker">What Floodplains will not do</p>
            <p>No opaque model-quality score.</p>
            <p>No recommended Manning values from a corpus average.</p>
            <p>No invented consultant parameters.</p>
            <p>No engineering certification.</p>
            <p className="muted small">A model polygon is an output, not ground truth. Calibration is not certainty.</p>
          </article>
        </div>
      </section>
    </>
  );
}
